import React, { useRef, useEffect, useContext, useState, useCallback } from 'react';
import { useStickyMode, Context } from '../../context/Context';
import { StyledSubPageNavigation } from './SubPageNavigationStyles';
import { getViewportSize, setHeight, throttle } from '../../utils/utils';
import StickyButtons from './ui/StickyButtons';
import Submenu from './submenu/Submenu';
import MainMenu from './mainmenu/MainMenu';
import { SUBMENUID, TRIGGERID } from '../../ContentType';

const SubPageNavigation: React.FC = () => {
  const {
    isPopOverOpen,
    setPopOverState,
    submenuDesktopRef,
    submenuMobileRef,
    submenuTriggerRef,
    showSubmenu,
    toggleShowSubmenu,
    setShowSubheadline,
    setTriggerId,
    setSubmenuId,
  } = useContext(Context);

  const stickyButtonsRef = useRef<HTMLDivElement>(null);
  const [showStickyBtns, setsShowStickyBtns] = useState(true);

  const stickymode = useStickyMode();
  const lastScrollTop = useRef(0);
  const scrollThreshold = 20;

  const renderStickyButtons = (show: boolean) => {
    if (stickyButtonsRef.current) {
      setHeight(stickyButtonsRef.current, show);
      setsShowStickyBtns(show);
    }
  };

  const renderMobileMenu = () => {
    if (submenuMobileRef.current) {
      setHeight(submenuMobileRef.current, showSubmenu);
    }
  };

  const renderDesktopMenu = () => {
    if (submenuDesktopRef.current) {
      setHeight(submenuDesktopRef.current, showSubmenu);
    }
  };

  const handleStickyButtonsOnScroll = () => {
    const element = stickyButtonsRef.current;
    if (!element) return;

    const currentScroll = window.scrollY;
    const scrollDiff = currentScroll - lastScrollTop.current;

    if (Math.abs(scrollDiff) < scrollThreshold) return; // Skip small scrolls

    const isScrollingUp = currentScroll < lastScrollTop.current;

    window.requestAnimationFrame(() => {
      setHeight(element, isScrollingUp);
      if (isScrollingUp) {
        setsShowStickyBtns(true);
      } else {
        setsShowStickyBtns(false);
      }
    });

    lastScrollTop.current = Math.max(0, currentScroll);
  };

  const setDesktopEventIds = () => {
    setTriggerId(TRIGGERID.DESKTOP);
    setSubmenuId(SUBMENUID.DESKTOP);
  };

  const setMobileEventIds = () => {
    setTriggerId(TRIGGERID.MOBILE);
    setSubmenuId(SUBMENUID.MOBILE);
  };

  useEffect(() => {
    const viewportSize = getViewportSize();

    if (viewportSize === 'mobile') {
      setShowSubheadline(true);
    } else if (viewportSize === 'tablet') {
      setShowSubheadline(true);
      setsShowStickyBtns(false);
    } else {
      toggleShowSubmenu();
    }
    renderMobileMenu();

    if (!showSubmenu && viewportSize === 'mobile') {
      // make a delay for ssr
      setTimeout(() => renderStickyButtons(true), 100);
    } else {
      setsShowStickyBtns(false);
    }
  }, []);

  useEffect(() => {
    renderMobileMenu();
    renderDesktopMenu();

    if (showSubmenu) {
      setsShowStickyBtns(false);
    }
  }, [showSubmenu]);

  const handleScroll = useCallback(
    throttle(() => {
      if (stickyButtonsRef.current) {
        handleStickyButtonsOnScroll();
      }
      if (showSubmenu && window.innerWidth >= 1024) {
        if (submenuDesktopRef?.current?.contains(document.activeElement)) {
          submenuTriggerRef.current?.focus();
        }
        toggleShowSubmenu();
      }
      setShowSubheadline(true);
    }, 150),

    [showSubmenu],
  );

  const handleResize = useCallback(() => {
    const viewportSize = getViewportSize();

    if (viewportSize === 'tablet' && showSubmenu) {
      toggleShowSubmenu();
      setShowSubheadline(true);
    }

    if (viewportSize === 'mobile' || viewportSize === 'tablet') {
      setMobileEventIds();
    } else {
      setDesktopEventIds();
    }

    setPopOverState(false);
    renderStickyButtons(false);
  }, [showSubmenu, setPopOverState]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleScroll, handleResize]);

  useEffect(() => {
    if (isPopOverOpen && showSubmenu) {
      toggleShowSubmenu();
    }
  }, [isPopOverOpen]);

  return (
    <StyledSubPageNavigation data-testid="subpage-navigation-container" isSticky={stickymode}>
      <MainMenu />
      <StickyButtons showStickyBtns={showStickyBtns} stickyButtonsRef={stickyButtonsRef} />
      <Submenu />
    </StyledSubPageNavigation>
  );
};
export default SubPageNavigation;
